import { jsPDF } from 'jspdf'

export interface QuotePdfData {
  tierLabel: string
  hourlyRate: number
  hours: number
  revisions: number
  marketLabel: string
  licenseLabel: string
  baseLabor: number
  revisionsPremium: number
  licenseAddon: number
  total: number
}

function fmt(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

export function generateQuotePdf(data: QuotePdfData) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 48
  let y = 0

  // Header band - Cosmic Slate deep background
  doc.setFillColor(7, 10, 19)
  doc.rect(0, 0, pageWidth, 120, 'F')

  // Brand mark
  doc.setFillColor(99, 102, 241)
  doc.circle(marginX + 10, 46, 10, 'F')
  doc.setFillColor(16, 185, 129)
  doc.circle(marginX + 22, 54, 6, 'F')

  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.text('Logocalculators', marginX + 40, 52)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(148, 163, 184)
  doc.text('Interactive Logo Quote & Pricing Estimator', marginX + 40, 68)

  doc.setFontSize(9)
  doc.setTextColor(110, 231, 183)
  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  doc.text(`Quote generated: ${dateStr}`, marginX, 100)

  y = 150

  // Section title
  doc.setTextColor(15, 23, 42)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text('Project Parameters', marginX, y)
  y += 10
  doc.setDrawColor(226, 232, 240)
  doc.line(marginX, y, pageWidth - marginX, y)
  y += 24

  const params: [string, string][] = [
    ['Expertise Tier', `${data.tierLabel} ($${data.hourlyRate}/hr)`],
    ['Expected Labor Hours', `${data.hours} hrs`],
    ['Revision Rounds', `${data.revisions}`],
    ['Geographic Market Index', data.marketLabel],
    ['Licensing Rights', data.licenseLabel],
  ]

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  params.forEach(([label, value]) => {
    doc.setTextColor(100, 116, 139)
    doc.text(label, marginX, y)
    doc.setTextColor(15, 23, 42)
    doc.setFont('helvetica', 'bold')
    doc.text(value, pageWidth - marginX, y, { align: 'right' })
    doc.setFont('helvetica', 'normal')
    y += 22
  })

  y += 14
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(15, 23, 42)
  doc.text('Itemized Quote Breakdown', marginX, y)
  y += 10
  doc.line(marginX, y, pageWidth - marginX, y)
  y += 24

  const items: [string, number][] = [
    ['Base Labor Fee', data.baseLabor],
    ['Revisions Premium', data.revisionsPremium],
    ['Licensing Add-on', data.licenseAddon],
  ]

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  items.forEach(([label, value]) => {
    doc.setTextColor(100, 116, 139)
    doc.text(label, marginX, y)
    doc.setTextColor(15, 23, 42)
    doc.text(fmt(value), pageWidth - marginX, y, { align: 'right' })
    y += 22
  })

  y += 12
  doc.setFillColor(240, 253, 244)
  doc.roundedRect(marginX, y, pageWidth - marginX * 2, 56, 8, 8, 'F')
  doc.setTextColor(6, 95, 70)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text('Total Estimate', marginX + 16, y + 34)
  doc.setFontSize(20)
  doc.text(fmt(data.total), pageWidth - marginX - 16, y + 36, { align: 'right' })

  y += 90
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(148, 163, 184)
  doc.text(
    'This estimate is generated by the Logocalculators Logo Pricing Calculator and is intended for',
    marginX,
    y
  )
  y += 14
  doc.text('informational and negotiation purposes only. Final project pricing may vary.', marginX, y)

  y += 30
  doc.setDrawColor(226, 232, 240)
  doc.line(marginX, y, pageWidth - marginX, y)
  y += 20
  doc.setTextColor(99, 102, 241)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.text('logocalculators.com', marginX, y)

  doc.save(`logocalculators-quote-${Date.now()}.pdf`)
}
